import React from 'react';
import { 
  UserPlus, 
  Heart, 
  Pill, 
  Activity, 
  Utensils, 
  CheckCircle, 
  ArrowRight,
  X,
  Sparkles
} from 'lucide-react';

interface OnboardingPromptProps {
  isVisible: boolean;
  onDismiss: () => void;
  onStart: () => void;
}

export default function OnboardingPrompt({
  isVisible,
  onDismiss,
  onStart
}: OnboardingPromptProps) {
  if (!isVisible) return null;

  const onboardingSteps = [
    {
      icon: UserPlus,
      title: 'Create Your Profile',
      description: 'Tell us about your health goals and preferences'
    },
    {
      icon: Heart,
      title: 'Add Health Conditions',
      description: 'Track your medical conditions and treatments'
    },
    {
      icon: Pill,
      title: 'Log Medications',
      description: 'Monitor prescriptions and check interactions'
    },
    {
      icon: Activity,
      title: 'Track Symptoms',
      description: 'Log symptoms and identify patterns'
    },
    {
      icon: Utensils,
      title: 'Record Meals',
      description: 'Track nutrition and dietary habits'
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Sparkles className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-slate-800">Welcome to ZippyHealth!</h2>
          </div>
          <button
            onClick={onDismiss}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="text-slate-600 mb-6">
          Let's personalize your health tracking experience. Complete a quick onboarding to get started with personalized insights and recommendations.
        </p>

        <div className="space-y-3 mb-6">
          {onboardingSteps.map((step, index) => {
            const IconComponent = step.icon;
            return (
              <div key={index} className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg">
                <div className="flex-shrink-0">
                  <IconComponent className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-slate-800">{step.title}</h3>
                  <p className="text-xs text-slate-600">{step.description}</p>
                </div>
                <CheckCircle className="h-4 w-4 text-green-500" />
              </div>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={onStart}
            className="flex-1 flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg font-medium transition-colors"
          >
            <UserPlus className="h-4 w-4" />
            <span>Start Onboarding</span>
            <ArrowRight className="h-4 w-4" />
          </button>
          
          <button
            onClick={onDismiss}
            className="flex-1 px-4 py-3 text-slate-600 hover:text-slate-800 font-medium transition-colors"
          >
            Maybe Later
          </button>
        </div>

        <p className="text-xs text-slate-500 mt-4 text-center">
          You can always complete onboarding later from your settings
        </p>
      </div>
    </div>
  );
} 